<template>
  <AppLayout>
    <div data-component="Lot/Layout">
      <aside data-component="Lot/Sidebar" class="w-full max-w-none">
      <header>
        <h1>NSIL {{ lot?.nsil || '' }}</h1>
        <p>Immeuble - {{ lot?.propertyCode || '' }}</p>
        <p>Lot - {{ lot?.buildingCode || '' }}</p>
      </header>

      <section v-if="isVisitableProperty" class="gestion-du-lot">
        <h2>Prochaine visite</h2>
        
        <div v-if="lot?.technicalManager?.id" class="gestionnaire-attribue">
          <h4>Gestionnaire attribué</h4>
          <div class="gestionnaire">
            {{ technicalManagerName || 'Gestionnaire non défini' }}
          </div>
          <p><small>{{ technicalManagerTeam }}</small></p>
          <p v-if="delegationText"><small>{{ delegationText }}</small></p>
          <a 
            @click.prevent="handleReassign" 
            class="link cursor-pointer"
          >
            Modifier
          </a>
        </div>
        
        <template v-else>
          <div class="gestionnaire-attribue">
            <h4>Gestionnaire attribué</h4>
          </div>
          <button
            @click="handleAssign"
            data-component="Widget/ActionButton"
            class="btn"
          >
            À attribuer
            <i class="ico assign"></i>
          </button>
        </template>

        <br />
        <h4>A visiter avant le</h4>
        <p><strong>{{ scheduledVisitDisplay || 'Non défini' }}</strong></p>

        <h4>Visite planifiée le</h4>
        <div v-if="planifiedAt" data-component="Widget/DatePickerButton">
          <div>
            <p><strong>{{ formatDate(planifiedAt) }}</strong></p>
            <p class="date-picker-wrapper">
              <label :for="`planify-modify-${lot?.id}`">
                <small>Modifier</small>
              </label>
              <input 
                type="date" 
                :id="`planify-modify-${lot?.id}`" 
                :value="formatDateForInput(planifiedAt)" 
                @change="planify" 
                class="date-input-hidden"
              />
            </p>
            <p @click="resetPlanification" class="cursor-pointer"><small>Réinitialiser</small></p>
          </div>
        </div>
        <div v-else class="date-picker-wrapper">
          <label :for="`planify-new-${lot?.id}`" data-component="Widget/ActionButton" class="btn date-picker-label">
            À planifier
            <i class="ico datepicker"></i>
          </label>
          <input 
            type="date" 
            :id="`planify-new-${lot?.id}`" 
            :value="planifiedAt || ''" 
            @change="planify" 
            class="date-input-hidden"
          />
        </div>
        
        <div v-if="shouldDisplayHeader" ref="floatingActionMenu" class="floating-action-menu" data-component="FloatingActionMenu">
          <button 
            @click="toggleActionMenu"
            class="btn fab-button"
            :class="{ 'active': isActionMenuOpen }"
          >
            <span class="fab-icon">+</span>
          </button>
          
          <transition name="fab-menu">
            <div v-if="isActionMenuOpen" class="fab-menu">
              <a
                href="#"
                class="fab-menu-item"
                @click.prevent="handleNewVisitClick"
              >
                <i class="fas fa-check-square"></i>
                <span>Nouvelle visite</span>
              </a>
              <router-link 
                v-if="!hidePreChiffrage && !hasPreChiffrage"
                :to="`/dossier/${lot?.id}/nouveau-pre-chiffrage`"
                class="fab-menu-item"
                @click="closeActionMenu"
              >
                <i class="fas fa-table"></i>
                <span>Nouveau pré-chiffrage</span>
              </router-link>
              <a
                v-else-if="!hidePreChiffrage && hasPreChiffrage"
                href="#"
                class="fab-menu-item fab-menu-item--disabled"
                @click.prevent
                title="Un pré-chiffrage existe déjà pour ce lot"
              >
                <i class="fas fa-table"></i>
                <span>Nouveau pré-chiffrage</span>
              </a>
            </div>
          </transition>
        </div>
      </section>

      <section class="caracteristique-du-lot">
        <h2>Caractéristiques du lot</h2>

        <div v-if="lot?.address" class="caracteristique-point">
          <p>{{ lot.address.street }}</p>
          <p v-if="lot.address.street2">{{ lot.address.street2 }}</p>
          <strong>{{ addressDisplay }}</strong>
        </div>

        <div class="caracteristique-point">
          <h3>{{ propertyTypeDisplay }}</h3>
          <em>{{ lot?.occupancy || '' }}</em>
        </div>

        <div v-if="lot?.legalOwner" class="caracteristique-point">
          <h3>Groupe - {{ lot.legalOwner.name }}</h3>
          <p><small>{{ lot.legalOwner.division || '' }}</small></p>
          <p><small>{{ lot?.lastOccupant?.name || '' }}</small></p>
        </div>
      </section>
      </aside>

      <div data-is="content">
      <nav v-if="shouldDisplayHeader" data-component="Lot/Header">
        <section class="nav-tabs">
          <ul class="flex">
            <li>
              <router-link
                :to="`/dossier/${lot?.id}/timeline?from=${encodedReferer}`"
                :class="isActive(/timeline$/) ? 'active' : ''"
              >
                <i class="ico timeline"></i>&nbsp;
                Historique
              </router-link>
            </li>
            <li>
              <router-link
                :to="`/dossier/${lot?.id}/informations?from=${encodedReferer}`"
                :class="isActive(/informations$/) ? 'active' : ''"
              >
                <i class="ico info"></i>&nbsp;
                Informations
              </router-link>
            </li>
            <li>
              <router-link
                :to="`/dossier/${lot?.id}/actions-et-travaux?from=${encodedReferer}`"
                :class="isActive(/actions-et-travaux$/) ? 'active' : ''"
              >
                <i class="ico actions"></i>&nbsp;
                Actions et travaux
              </router-link>
            </li>
          </ul>
        </section>
        
        <span data-component="Widget/FlatButton">
          <router-link 
            :to="referer" 
            @click="onCloseClick"
          >
            Fermer
            &nbsp;<i class="ico close"></i>
          </router-link>
        </span>
      </nav>

      <div class="page-content">
        <router-view />
      </div>
    </div>
  </div>
  </AppLayout>

  <!-- Modale pare-feu : visite récente -->
  <RecentVisitWarningModal
    :visible="recentVisitWarning.visible"
    :lastVisitDate="recentVisitWarning.lastVisitDate"
    @confirm="handleRecentVisitConfirm"
    @cancel="handleRecentVisitCancel"
  />
</template>

<script>
import { mapGetters } from 'vuex'
import moment from 'moment'
import AppLayout from '../../components/Layout/AppLayout.vue'
import RecentVisitWarningModal from '../../components/Common/RecentVisitWarningModal.vue'
import dexieService from '@/services/dexieService'
import { openAttributionModal } from '@/utils/attributionHelper'
import { getEnv } from '@/utils/env'
import {
  formatDate,
  isVisitableProperty,
  getVisitStatusValue,
  getDelegationText
} from '@/utils/lot'

export default {
  name: 'LotLayout',
  components: {
    AppLayout,
    RecentVisitWarningModal
  },
  props: {
    displayHeader: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      referer: '/',
      encodedReferer: '/',
      visitStatuses: [],
      userFromDexie: null,
      loadingUser: false,
      propertyTypes: {},
      assistanceMandators: {},
      managerMandators: {},
      currentLotFromDexie: null,
      loadingLot: false,
      isActionMenuOpen: false,
      // Pare-feu "nouvelle visite"
      recentVisitWarning: {
        visible: false,
        lastVisitDate: null,
        targetPath: null
      }
    }
  },
  mounted() {
    document.addEventListener('click', this.handleOutsideClick)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick)
  },
  computed: {
    ...mapGetters('login', ['user']),
    ...mapGetters('attribution', ['lastAttributionUpdate']),
    
    lot() {
      return this.currentLotFromDexie
    },
    
    isVisitableProperty() {
      if (!this.lot) return false
      return isVisitableProperty(this.lot.scheduledVisitStatus)
    },
    
    shouldDisplayHeader() {
      // Ne pas afficher le header sur la page nouvelle-visite, nouveau-pre-chiffrage et fiche visite
      return this.displayHeader && 
             !this.$route.path.includes('/nouvelle-visite') &&
             !this.$route.path.includes('/nouveau-pre-chiffrage') &&
             !this.$route.path.includes('/fiche/')
    },
    
    planifiedAt() {
      return this.lot?.planifiedAt || ''
    },
    
    // Gestionnaire attribué avec nom complet
    technicalManagerName() {
      // Priorité 1: Données récupérées via Dexie
      if (this.userFromDexie) {
        if (this.userFromDexie.fullName) {
          return this.userFromDexie.fullName
        }
        
        if (this.userFromDexie.firstName && this.userFromDexie.lastName) {
          return `${this.userFromDexie.firstName} ${this.userFromDexie.lastName}`
        }
      }
      
      // Priorité 2: Données du lot (fallback)
      if (!this.lot?.technicalManager) {
        return ''
      }
      
      const manager = this.lot.technicalManager
      
      if (manager.fullName) {
        return manager.fullName
      }
      
      if (manager.firstName && manager.lastName) {
        return `${manager.firstName} ${manager.lastName}`
      }
      
      return manager.name || 'Nom non disponible'
    },
    
    // Équipe du gestionnaire
    technicalManagerTeam() {
      // Priorité 1: Données récupérées via Dexie
      if (this.userFromDexie && this.userFromDexie.team) {
        return this.userFromDexie.team.name || ''
      }
      
      // Priorité 2: Données du lot (fallback)
      return this.lot?.technicalManager?.team?.name || ''
    },
    
    // Texte de délégation
    delegationText() {
      if (!this.lot) return ''
      return getDelegationText(this.lot)
    },
    
    // Date de visite formatée avec dictionnaire
    scheduledVisitDisplay() {
      if (!this.lot) return ''
      
      // Priorité 1: scheduledVisitStatus transformé via le dictionnaire
      if (this.lot.scheduledVisitStatus) {
        const transformedValue = getVisitStatusValue(this.lot.scheduledVisitStatus, this.visitStatuses)
        if (transformedValue && transformedValue !== '') {
          return transformedValue
        }
      }
      
      // Priorité 2: scheduledVisitAt comme date formatée
      if (this.lot.scheduledVisitAt) {
        return formatDate(this.lot.scheduledVisitAt)
      }
      
      return ''
    },
    
    // Type de propriété transformé via le dictionnaire
    propertyTypeDisplay() {
      if (!this.lot) return ''
      
      const typeCode = this.lot.type || ''
      
      if (this.propertyTypes && this.propertyTypes[typeCode]) {
        return this.propertyTypes[typeCode]
      }
      
      return typeCode || 'Non défini'
    },
    
    // Assistance Mandator
    assistanceMandatorDisplay() {
      if (!this.lot) return ''
      
      const mandatorId = this.lot.assistance?.mandator?.id
      if (!mandatorId) return ''
      
      const mandatorName = this.assistanceMandators[mandatorId]
      return mandatorName || ''
    },
    
    // Manager Mandator
    managerMandatorDisplay() {
      if (!this.lot) return ''
      
      const mandatorId = this.lot.manager?.mandator?.id
      if (!mandatorId) return ''
      
      const mandatorData = this.managerMandators[mandatorId]
      if (!mandatorData) return ''
      
      const name = mandatorData.name || ''
      const division = mandatorData.division || ''
      
      if (!name) return division || ''
      if (!division) return name
      if (name.includes(division)) return name
      
      return `${name} - ${division}`
    },
    
    // Legal Owner avec division
    legalOwnerDisplay() {
      if (!this.lot) return ''
      
      const legalOwner = this.lot.legalOwner
      if (!legalOwner) return ''
      
      const name = legalOwner.name || ''
      const division = legalOwner.division || ''
      
      if (!name) return division || ''
      if (!division) return name
      if (name === division || name.includes(division)) return name
      
      return `${name} - ${division}`
    },
    
    // Masquer le bouton "Pré-chiffrage" selon la variable d'environnement
    hidePreChiffrage() {
      return getEnv('VUE_APP_HIDE_PRE_CHIFFRAGE') === 'true'
    },

    // Désactiver le bouton "Nouveau pré-chiffrage" si un pré-chiffrage existe déjà
    hasPreChiffrage() {
      return (this.lot?.preChiffrages?.length ?? 0) > 0
    },

    // Adresse formatée (code postal, ville)
    addressDisplay() {
      if (!this.lot?.address) return ''
      
      const zipCode = this.lot.address.zipCode || ''
      const city = this.lot.address.city || ''
      
      if (!zipCode && !city) return ''
      if (!zipCode) return city
      if (!city) return zipCode
      
      return `${zipCode}, ${city}`
    }
  },
  
  async created() {
    // Charger les dictionnaires et mandataires
    await this.loadVisitStatuses()
    await this.loadPropertyTypes()
    await this.loadAssistanceMandators()
    await this.loadManagerMandators()
    
    // Charger les données du lot directement depuis Dexie
    const lotId = this.$route.params.id
    if (lotId) {
      await this.loadLotFromDexie(lotId)
    }
    
    // Charger l'utilisateur depuis Dexie si un gestionnaire est attribué
    if (this.lot?.technicalManager?.id) {
      await this.loadUserFromDexie(this.lot.technicalManager.id)
    }
    
    // Configurer les URLs de retour
    // 'from' contient le chemin d'origine (ex: /parc-immobilier)
    // Les filtres sont restaurés automatiquement par le router via filterStorageService
    this.referer = this.$route.query.from || '/parc-immobilier'
    this.encodedReferer = encodeURIComponent(this.referer)
  },
  
  watch: {
    // Surveiller les changements de route pour recharger le lot
    '$route.params.id': {
      async handler(newId) {
        if (newId) {
          await this.loadLotFromDexie(newId)
        }
      }
    },
    
    // Surveiller les changements de route path (pour détecter le retour depuis nouvelle-visite)
    '$route.path': {
      async handler(newPath, oldPath) {
        // Si on revient de nouvelle-visite vers timeline/informations/actions-et-travaux
        // on doit recharger le lot pour avoir scheduledVisitAt mis à jour
        const lotId = this.$route.params.id
        if (lotId && oldPath && oldPath.includes('/nouvelle-visite') && !newPath.includes('/nouvelle-visite')) {
          await this.loadLotFromDexie(lotId)
        }
      }
    },
    
    // Surveiller les changements du gestionnaire technique
    'lot.technicalManager.id': {
      handler(newId) {
        if (newId) {
          this.loadUserFromDexie(newId)
        } else {
          this.userFromDexie = null
        }
      }
    },
    
    // Surveiller les mises à jour d'attribution
    lastAttributionUpdate(newVal) {
      if (newVal) {
        // Recharger le lot quand une attribution a été modifiée
        const lotId = this.$route.params.id
        if (lotId) {
          this.loadLotFromDexie(lotId)
        }
      }
    }
  },
  
  methods: {
    async loadPropertyTypes() {
      try {
        const dexieService = (await import('@/services/dexieService')).default
        let cachedTypes = await dexieService.getDictionary('property_type')
        
        // Vérifier si les données sont dans la structure de l'ancienne app (avec items)
        if (cachedTypes && cachedTypes.items && Array.isArray(cachedTypes.items)) {
          // Convertir le tableau items en objet clé-valeur
          const typeMap = {}
          cachedTypes.items.forEach(item => {
            if (item.key && item.value) {
              typeMap[item.key] = item.value
            }
          })
          this.propertyTypes = typeMap
          return
        }
        
        // Sinon, vérifier si c'est déjà un objet clé-valeur
        if (cachedTypes && Object.keys(cachedTypes).length > 0) {
          this.propertyTypes = cachedTypes
          return
        }
        
        this.propertyTypes = {}
      } catch (error) {
        console.error('Erreur lors du chargement des types de propriété:', error)
        this.propertyTypes = {}
      }
    },
    
    async loadVisitStatuses() {
      try {
        const dexieService = (await import('@/services/dexieService')).default
        let cachedStatuses = await dexieService.getDictionary('visit_status')
        
        // Vérifier si les données sont dans la structure de l'ancienne app (avec items)
        if (cachedStatuses && cachedStatuses.items && Array.isArray(cachedStatuses.items)) {
          this.visitStatuses = cachedStatuses.items
          return
        }
        
        // Sinon, vérifier si c'est déjà un objet clé-valeur et le convertir en tableau
        if (cachedStatuses && Object.keys(cachedStatuses).length > 0) {
          this.visitStatuses = Object.entries(cachedStatuses).map(([key, value]) => ({ key, value }))
          return
        }
        
        this.visitStatuses = []
      } catch (error) {
        console.error('Erreur lors du chargement des statuts de visite:', error)
        this.visitStatuses = []
      }
    },
    
    async loadAssistanceMandators() {
      try {
        const { assistanceMandators } = await import('@/services/dexieService')
        
        const mandators = await assistanceMandators().toArray()
        
        const mandatorsMap = {}
        mandators.forEach(mandator => {
          if (mandator.id && mandator.name) {
            mandatorsMap[mandator.id] = mandator.name
          }
        })
        
        this.assistanceMandators = mandatorsMap
      } catch (error) {
        console.error('Erreur lors du chargement des assistanceMandators:', error)
        this.assistanceMandators = {}
      }
    },
    
    async loadManagerMandators() {
      try {
        const { managerMandators } = await import('@/services/dexieService')
        
        const mandators = await managerMandators().toArray()
        
        const mandatorsMap = {}
        mandators.forEach(mandator => {
          if (mandator.id) {
            mandatorsMap[mandator.id] = {
              name: mandator.name || '',
              division: mandator.division || ''
            }
          }
        })
        
        this.managerMandators = mandatorsMap
      } catch (error) {
        console.error('Erreur lors du chargement des managerMandators:', error)
        this.managerMandators = {}
      }
    },
    
    isActive(regex) {
      return regex.test(this.$route.path)
    },
    
    onCloseClick() {
      this.$router.push(this.referer)
    },
    
    async planify(event) {
      const date = event.target.value
      if (date && this.lot?.id) {
        try {
          // Envoyer la date au format ISO YYYY-MM-DD (attendu par le backend)
          const formattedDate = moment(date, 'YYYY-MM-DD').format('YYYY-MM-DD')
          
          // Utiliser le mutation service pour tenter l'API puis fallback vers mutations
          const mutationService = (await import('@/services/mutationService')).default
          const result = await mutationService.updatePropertyPlanification(this.lot.id, formattedDate)
          
          if (result.success) {
            // Recharger le lot pour afficher la nouvelle date
            await this.loadLotFromDexie(this.lot.id)
            
          } else {
            console.error('Erreur lors de la planification:', result.error)
            alert('Erreur lors de l\'enregistrement de la date de planification')
          }
        } catch (error) {
          console.error('Erreur lors de la planification de la visite:', error)
          alert('Erreur lors de l\'enregistrement de la date de planification')
        }
      }
    },
    
    async resetPlanification() {
      if (this.lot?.id) {
        try {
          // Utiliser le mutation service pour tenter l'API puis fallback vers mutations
          const mutationService = (await import('@/services/mutationService')).default
          const result = await mutationService.updatePropertyPlanification(this.lot.id, '')
          
          if (result.success) {
            // Recharger le lot
            await this.loadLotFromDexie(this.lot.id)
            
          } else {
            console.error('Erreur lors de la réinitialisation:', result.error)
            alert('Erreur lors de la réinitialisation de la planification')
          }
        } catch (error) {
          console.error('Erreur lors de la réinitialisation de la planification:', error)
          alert('Erreur lors de la réinitialisation de la planification')
        }
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return ''
      
      // Essayer différents formats possibles
      const formats = ['DD/MM/YYYY', 'YYYY-MM-DD', 'DD-MM-YYYY', moment.ISO_8601]
      const parsedDate = moment(dateString, formats, true)
      
      if (!parsedDate.isValid()) {
        console.warn('Date invalide:', dateString)
        return ''
      }
      
      return parsedDate.format('DD/MM/YYYY')
    },
    
    formatDateForInput(dateString) {
      if (!dateString) return ''
      
      // Essayer différents formats possibles
      const formats = ['DD/MM/YYYY', 'YYYY-MM-DD', 'DD-MM-YYYY', moment.ISO_8601]
      const parsedDate = moment(dateString, formats, true)
      
      if (!parsedDate.isValid()) {
        console.warn('Date invalide pour input:', dateString)
        return ''
      }
      
      return parsedDate.format('YYYY-MM-DD')
    },
    
    async loadLotFromDexie(lotId) {
      if (!lotId) return
      
      try {
        this.loadingLot = true
        
        // Récupérer la propriété depuis Dexie
        const property = await dexieService.getProperty(lotId)
        
        if (property) {
          this.currentLotFromDexie = property
        } else {
          console.warn(`Lot avec l'ID ${lotId} non trouvé dans Dexie`)
          this.currentLotFromDexie = null
        }
      } catch (error) {
        console.error('Erreur lors de la récupération du lot depuis Dexie:', error)
        this.currentLotFromDexie = null
      } finally {
        this.loadingLot = false
      }
    },
    
    async loadUserFromDexie(userId) {
      if (!userId) return
      
      try {
        this.loadingUser = true
        
        // Récupérer l'utilisateur depuis la table users de Dexie
        const user = await dexieService.users().get(userId)
        
        if (user) {
          this.userFromDexie = user
        } else {
          console.warn(`Utilisateur avec l'ID ${userId} non trouvé dans Dexie`)
          this.userFromDexie = null
        }
      } catch (error) {
        console.error('Erreur lors de la récupération de l\'utilisateur depuis Dexie:', error)
        this.userFromDexie = null
      } finally {
        this.loadingUser = false
      }
    },
    
    handleAssign() {
      // Ouvrir le modal d'attribution avec l'utilisateur courant pré-sélectionné
      openAttributionModal(this.$store, {
        lots: [this.lot?.id],
        teamId: this.user?.team?.id || null,
        managerId: this.user?.id || null
      })
    },
    
    handleReassign() {
      // Ouvrir le modal d'attribution avec le gestionnaire actuel pré-sélectionné
      const teamId = this.userFromDexie?.team?.id || this.lot?.technicalManager?.team?.id || null
      const managerId = this.lot?.technicalManager?.id || null
      
      openAttributionModal(this.$store, {
        lots: [this.lot?.id],
        teamId,
        managerId
      })
    },
    
    toggleActionMenu() {
      this.isActionMenuOpen = !this.isActionMenuOpen
    },
    
    closeActionMenu() {
      this.isActionMenuOpen = false
    },

    handleOutsideClick(event) {
      if (this.isActionMenuOpen && this.$refs.floatingActionMenu && !this.$refs.floatingActionMenu.contains(event.target)) {
        this.closeActionMenu()
      }
    },

    // ── Pare-feu "Nouvelle visite" ──────────────────────────────────────────

    /**
     * Intercepte le clic sur "Nouvelle visite".
     * Vérifie si une visite a été faite il y a moins de 15 jours pour ce lot.
     * Si oui → affiche la modale d'avertissement.
     * Sinon → navigue directement.
     */
    async handleNewVisitClick() {
      this.closeActionMenu()

      if (!this.lot?.id) {
        this.$router.push(`/dossier/${this.$route.params.id}/nouvelle-visite`)
        return
      }

      const targetPath = `/dossier/${this.lot.id}/nouvelle-visite`
      const FIREWALL_DAYS = 15

      try {
        // ── 1. Visites synchronisées (stockées dans property.visits) ──────────
        const property = await dexieService.getProperty(this.lot.id)
        const syncedVisits = (property?.visits || [])
          .filter(v => !v.toDelete && (v.date || v.createdAt))
          .map(v => v.date || v.createdAt)

        // ── 2. Visites en attente de synchronisation (table mutations) ─────────
        const { mutations } = await import('@/services/dexieService')
        const pendingMutations = await mutations()
          .filter(m =>
            m.entity === 'visit' &&
            (
              m.data?.propertyId === this.lot.id ||
              m.data?.property?.id === this.lot.id
            )
          )
          .toArray()

        const pendingVisitDates = pendingMutations
          .map(m => m.data?.date || m.data?.createdAt || m.data?.timestamp || m.createdAt || null)
          .filter(Boolean)

        // ── 3. Fusionner toutes les dates et trouver la plus récente ───────────
        const allDates = [...syncedVisits, ...pendingVisitDates]

        if (allDates.length === 0) {
          // Aucune visite connue → navigation directe
          this.$router.push(targetPath)
          return
        }

        const mostRecentDate = allDates.reduce((latest, d) => {
          return moment(d).isAfter(moment(latest)) ? d : latest
        })

        const daysAgo = moment().diff(moment(mostRecentDate), 'days')

        if (daysAgo < FIREWALL_DAYS) {
          // Visite trop récente → afficher la modale
          this.recentVisitWarning = {
            visible: true,
            lastVisitDate: mostRecentDate,
            targetPath
          }
        } else {
          // Dernière visite suffisamment ancienne → navigation directe
          this.$router.push(targetPath)
        }
      } catch (error) {
        console.error('[LotLayout] Erreur lors de la vérification des visites récentes:', error)
        // En cas d'erreur, ne pas bloquer l'utilisateur
        this.$router.push(targetPath)
      }
    },

    /** L'utilisateur confirme qu'il souhaite quand même créer la visite */
    handleRecentVisitConfirm() {
      const path = this.recentVisitWarning.targetPath
      this.recentVisitWarning = { visible: false, lastVisitDate: null, targetPath: null }
      if (path) {
        this.$router.push(path)
      }
    },

    /** L'utilisateur annule → fermer la modale sans naviguer */
    handleRecentVisitCancel() {
      this.recentVisitWarning = { visible: false, lastVisitDate: null, targetPath: null }
    }
  }
}
</script>

<style scoped lang="scss">
@import './LotLayout.scss';
</style>
